/**
 * Sprint D — Money formatting
 * ============================
 * Format a price for display. Supports two API shapes during the 2-week
 * cutover window (Sprint D → Sprint E):
 *
 * **NEW API (preferred for new code)** — pass a `Money` object:
 *   formatPrice({ valueMinor: 49000, currency: 'SEK' })   // → "490 kr"
 *   formatPrice({ valueMinor: 49050, currency: 'SEK' })   // → "490,50 kr"
 *   formatPrice({ valueMinor: 0, currency: 'SEK' })       // → "0 kr"
 *   formatPrice(null)                                      // → "–"
 *
 * Reads `*_minor` columns directly. No mental conversion needed at call-site.
 *
 * **LEGACY API (kept for 2 weeks while DB cols still contain kronor)** —
 * pass a raw kronor number:
 *   formatPrice(490)                                       // → "490 kr"
 *   formatPrice(99.7, { showDecimals: true })              // → "99,70 kr"
 *
 * Equivalent to calling `formatPriceLegacy(490)`. Will emit a `console.warn`
 * in dev builds to nudge migration.
 *
 * **Migration guide for call-sites**
 * ----------------------------------
 * BEFORE:  formatPrice(row.price_total)
 * AFTER:   formatPrice({ valueMinor: row.price_total_minor, currency: row.currency ?? 'SEK' })
 *
 * Sprint E will:
 *   1. Remove the legacy number overload
 *   2. Drop `formatPriceLegacy`
 *   3. Drop legacy DB columns
 */

// ────────────────────────────────────────────────────────────────────────────
// Public types
// ────────────────────────────────────────────────────────────────────────────

export type Money = {
  /** Amount in minor currency units (öre for SEK). */
  valueMinor: number;
  /** ISO 4217 currency code, e.g. "SEK". */
  currency: string;
};

export type FormatPriceLegacyOptions = {
  /** Append " kr" suffix. Default: true. */
  showCurrency?: boolean;
  /** Render with 2 decimals. Default: false (rounds). */
  showDecimals?: boolean;
};

// ────────────────────────────────────────────────────────────────────────────
// formatPrice — overloaded
// ────────────────────────────────────────────────────────────────────────────

export function formatPrice(money: Money | null | undefined): string;
export function formatPrice(
  kronor: number,
  options?: FormatPriceLegacyOptions,
): string;
export function formatPrice(
  arg: Money | number | null | undefined,
  options?: FormatPriceLegacyOptions,
): string {
  // Empty case
  if (arg == null) {
    return "–";
  }

  // Legacy number path
  if (typeof arg === "number") {
    return formatPriceLegacy(arg, options);
  }

  // New Money path — use Intl.NumberFormat for locale-correct currency display
  if (typeof arg === "object" && "valueMinor" in arg && "currency" in arg) {
    return formatMoneyIntl(arg);
  }

  // Defensive: shouldn't be reachable with TS in place
  return "–";
}

// ────────────────────────────────────────────────────────────────────────────
// formatPriceLegacy — explicit legacy API for callers that prefer clarity
// ────────────────────────────────────────────────────────────────────────────

/**
 * Format a value already expressed in kronor.
 *
 * @deprecated Pass `Money` to `formatPrice` instead. Removed in Sprint E.
 *
 * @param amountKronor — Numeric kronor value (may include decimals).
 * @param options — `showCurrency` (default true) appends " kr"; `showDecimals` (default false) renders with 2 decimals.
 */
export function formatPriceLegacy(
  amountKronor: number,
  options?: FormatPriceLegacyOptions,
): string {
  if (typeof window !== "undefined" && import.meta.env?.DEV) {
    // Throttled to once-per-stack — every call still runs full logic.
    const stack = new Error().stack?.split("\n")[2]?.trim() ?? "(unknown)";
    if (!warnedStacks.has(stack)) {
      warnedStacks.add(stack);
      console.warn(
        `[formatPrice] Legacy kronor-number form used at ${stack}. ` +
          `Migrate to formatPrice({ valueMinor, currency }) — Sprint E removes this overload.`,
      );
    }
  }

  const { showCurrency = true, showDecimals = false } = options ?? {};
  const formatted = showDecimals
    ? amountKronor.toLocaleString("sv-SE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : Math.round(amountKronor).toLocaleString("sv-SE");

  return showCurrency ? `${formatted} kr` : formatted;
}

const warnedStacks = new Set<string>();

// ────────────────────────────────────────────────────────────────────────────
// formatMoneyIntl — internal renderer for the Money path
// ────────────────────────────────────────────────────────────────────────────

function formatMoneyIntl(money: Money): string {
  const { valueMinor, currency } = money;

  // valueMinor may be NaN for legacy NULL data; render as em-dash.
  if (typeof valueMinor !== "number" || Number.isNaN(valueMinor)) {
    return "–";
  }

  // Currency divisor — SEK + most major currencies use 100 minor per major.
  // (CLP, JPY etc. use 1, but Boply is SEK-only per binding decision §0.5.)
  const decimalValue = valueMinor / 100;

  // Whole-kronor render (no decimals) when the value is exactly divisible by 100.
  // Otherwise show 2 decimals so 490,50 kr doesn't get rounded to 491 kr.
  const hasFractional = valueMinor % 100 !== 0;

  // Use sv-SE NumberFormat for locale-correct grouping + decimal-comma.
  // We use 'currency' style for the SEK suffix (renders as " kr" in sv-SE).
  try {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      minimumFractionDigits: hasFractional ? 2 : 0,
      maximumFractionDigits: hasFractional ? 2 : 0,
    }).format(decimalValue);
  } catch {
    // Defensive — bad currency code falls back to plain rendering with kr suffix.
    const formatted = hasFractional
      ? decimalValue.toLocaleString("sv-SE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : Math.round(decimalValue).toLocaleString("sv-SE");
    return `${formatted} kr`;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Conversion helpers (kept compatible with pre-Sprint-D usage)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Convert öre → kronor (rounded to nearest whole krona).
 * Used by code that needs an integer kronor value (e.g. SIE writer pre-Sprint D).
 *
 * Prefer `formatPrice({ valueMinor })` for display. Use `oreToKronorDecimal`
 * when you need decimal precision.
 */
export function oreToKronor(ore: number): number {
  return Math.round(ore / 100);
}

/**
 * Convert öre → kronor (decimal, no rounding).
 * Use when emitting accounting output (SIE, CSV, invoices) that must match
 * the original amount exactly to the öre.
 */
export function oreToKronorDecimal(ore: number): number {
  return ore / 100;
}

/**
 * Convert kronor → öre.
 *
 * Used when sending values to Stripe API or any DB column with `_minor` suffix.
 * Handles decimal kronor inputs via Math.round.
 */
export function kronorToOre(kronor: number): number {
  return Math.round(kronor * 100);
}

// ────────────────────────────────────────────────────────────────────────────
// Convenience helper for the common "make a Money from a row" pattern
// ────────────────────────────────────────────────────────────────────────────

/**
 * Build a Money object from a row that has `*_minor` and `currency` columns.
 * Returns `null` when the value is missing — `formatPrice(null)` then renders
 * an em-dash placeholder.
 *
 * Example:
 *   formatPrice(toMoney(row.price_total_minor, row.currency))
 *
 * Default currency is 'SEK' for rows that haven't been backfilled yet (Phase 3
 * adds the column with DEFAULT 'SEK', so this fallback is rarely needed).
 */
export function toMoney(
  valueMinor: number | null | undefined,
  currency: string | null | undefined = "SEK",
): Money | null {
  if (valueMinor == null) return null;
  return { valueMinor, currency: currency ?? "SEK" };
}

/**
 * Sprint D dual-state resolver — prefer `valueMinor` (post-migration öre);
 * fall back to legacy kronor column when `valueMinor` is null/undefined.
 *
 * Mirrors `supabase/functions/_shared/formatMoney.ts:resolveMoney` —
 * keep the two implementations behaviorally identical. Sprint E removes
 * both copies (legacy column gone → only valueMinor path remains).
 *
 * Throws when either value is NaN/non-finite (data corruption fail-fast).
 */
export function resolveMoney(
  valueMinor: number | null | undefined,
  legacyKronor: number | null | undefined,
  currency: string | null | undefined = "SEK",
): Money | null {
  if (typeof valueMinor === "number" && Number.isFinite(valueMinor)) {
    return { valueMinor, currency: currency ?? "SEK" };
  }
  if (typeof legacyKronor === "number" && Number.isFinite(legacyKronor)) {
    return { valueMinor: Math.round(legacyKronor * 100), currency: currency ?? "SEK" };
  }
  return null;
}
